import type { DueItem, DueTarget } from "@kundeninfo/api-client";
import { addDays, isoWeek, weekday } from "../tickets/timelineModel.js";
import { tageZwischen } from "../tickets/ticketFormat.js";
import { amountText, compareDueItems, recedesBehindTicket, ticketDueIds } from "../termine/dueItems.js";
import { DUE_ITEM_TEXT } from "../termine/dueItemTexts.js";
import { CALENDAR_TEXT } from "./calendarTexts.js";

/**
 * MODELL DES KALENDERS (Spec D23, D26; Plan T22) — reine Funktionen, kein React, kein DOM: Sichten und ihre Fenster,
 * Monats- und Jahresraster, Blättern, Einträge je Tag, Doppelte, Zeitraum- und Vorlesetexte. Alle Tage sind Berliner
 * Kalendertage als ISO-Zeichenkette (`2026-11-05`); gerechnet wird mit `addDays` aus `timelineModel.ts` (UTC wie
 * `ticketFormat.ts`). Die Sichten arbeiten auf `CalendarEntry` — heute aus `DueItem` (`entryFromDueItem`); mit K3 kommt
 * ein Adapter für Termine (`APPOINTMENT`) dazu, Raster, Sichten und Tastatur bleiben.
 */

export const CALENDAR_VIEWS = ["DAY", "WEEK", "MONTH", "YEAR"] as const;
export type CalendarView = (typeof CALENDAR_VIEWS)[number];
/** Segment der Adresse je Sicht (T22): `/kalender/<segment>/<datum>`. */
export const VIEW_SEGMENT: Readonly<Record<CalendarView, "tag" | "woche" | "monat" | "jahr">> =
  { DAY: "tag", WEEK: "woche", MONTH: "monat", YEAR: "jahr" };
/** Höchstens so viele Einträge je Monatszelle, dann „mehr“ (D23, Hausregel 547). */
export const MONTH_CELL_MAX = 3;

/** Die Einträge einer Monatszelle bei `rows` Rasterzeilen (G2): sechs Zeilen zeigen einen weniger — so passt auch ihr Raster
 *  bei 1 280 × 900 ohne Rollen ins Rollfeld (die Zellhöhe dazu steht in `Kalender.css`, `data-rows`). */
export function monthCellMax(rows: number): number {
  return rows >= 6 ? MONTH_CELL_MAX - 1 : MONTH_CELL_MAX;
}
/** Höchstens so viele Einträge im Sofort-Popup eines Jahrestags, dann „+n weitere“. */
const YEAR_POPUP_MAX = 10;

/** Ein Eintrag des Kalenders: Tage (über mehrere Tage mit `endDate`, Vorgriff K3), Texte, Zustand und Ziel. */
export type CalendarEntry = {
  key: string; source: string; date: string; endDate: string | null; time: string | null; endTime: string | null;
  reference: string | null; title: string; subtitle: string | null; amount: string | null;
  state: "OVERDUE" | "DUE_TODAY" | "OPEN" | "DONE"; target: DueTarget; ticket: DueItem["ticket"]; item: DueItem;
};

/** Der Adapter für die Terminebene: Untertitel ist der Partner; ohne Titel (Eingangsrechnung ohne Vertrag) die Nummer
 *  bzw. das Quellwort, damit kein leerer Knopf entsteht (wie der Abschnitt „Termine“). */
export function entryFromDueItem(item: DueItem): CalendarEntry {
  return { key: item.key, source: item.source, date: item.date, endDate: item.endDate, time: item.time, endTime: item.endTime,
    reference: item.reference, title: item.title !== "" ? item.title : item.reference ?? DUE_ITEM_TEXT.wordOf(item),
    subtitle: item.partner?.name ?? null, amount: amountText(item), state: item.state, target: item.target,
    ticket: item.ticket, item };
}

// ── Kalendertage ───────────────────────────────────────────────────────────────────────────────────────────────

const two = (n: number): string => String(n).padStart(2, "0");
const parts = (day: string): [number, number, number] => day.split("-").map(Number) as [number, number, number];
const isoDay = (y: number, m: number, d: number): string => `${y}-${two(m)}-${two(d)}`;
const daysInMonth = (y: number, m: number): number => new Date(Date.UTC(y, m, 0)).getUTCDate();
/** `05.11.` bzw. `05.11.2026`. */
const shortDate = (day: string): string => `${day.slice(8, 10)}.${day.slice(5, 7)}.`;
const fullDate = (day: string): string => `${shortDate(day)}${day.slice(0, 4)}`;

/** Derselbe Tag n Monate weiter; fehlt er dort (31. → Februar), der Monatsletzte. */
function plusMonths(day: string, n: number): string {
  const [y, m, d] = parts(day);
  const index = y * 12 + m - 1 + n;
  const ny = Math.floor(index / 12), nm = (index % 12) + 1;
  return isoDay(ny, nm, Math.min(d, daysInMonth(ny, nm)));
}

// ── Fenster und Raster (D23) ───────────────────────────────────────────────────────────────────────────────────

/** Montag bis Sonntag, Woche für Woche: von Montag vor dem 1. bis Sonntag nach dem Letzten (4–6 Zeilen, höchstens 42 Tage). */
export function monthGrid(anchor: string): { kw: number; days: string[] }[] {
  const [y, m] = parts(anchor);
  const first = isoDay(y, m, 1), last = isoDay(y, m, daysInMonth(y, m));
  const start = addDays(first, -weekday(first));
  const weeks = (tageZwischen(start, addDays(last, 6 - weekday(last))) + 1) / 7;
  return Array.from({ length: weeks }, (_, w) => {
    const days = Array.from({ length: 7 }, (__, d) => addDays(start, w * 7 + d));
    return { kw: isoWeek(days[0]!), days };
  });
}

/** Die zwölf Monate eines Jahres (`month` = `2026-02`) mit ihren Tagen — 365 bzw. 366. */
export function yearMonths(year: number): { month: string; days: string[] }[] {
  return Array.from({ length: 12 }, (_, i) => ({ month: `${year}-${two(i + 1)}`,
    days: Array.from({ length: daysInMonth(year, i + 1) }, (__, d) => isoDay(year, i + 1, d + 1)) }));
}

/** Das Fenster einer Sicht um den Anker (D23): Tag; Montag bis Sonntag; Rasterwochen; 1.1.–31.12. */
export function windowOf(view: CalendarView, anchor: string): { from: string; to: string } {
  switch (view) {
    case "DAY": return { from: anchor, to: anchor };
    case "WEEK": { const from = addDays(anchor, -weekday(anchor)); return { from, to: addDays(from, 6) }; }
    case "MONTH": { const g = monthGrid(anchor); return { from: g[0]!.days[0]!, to: g.at(-1)!.days[6]! }; }
    case "YEAR": return { from: `${anchor.slice(0, 4)}-01-01`, to: `${anchor.slice(0, 4)}-12-31` };
  }
}

/** Blättern um eine Periode: Tag, sieben Tage, Monat bzw. Jahr (Monatsletzter, wenn der Tag fehlt). */
export function shiftAnchor(view: CalendarView, anchor: string, dir: -1 | 1): string {
  switch (view) {
    case "DAY": return addDays(anchor, dir);
    case "WEEK": return addDays(anchor, 7 * dir);
    case "MONTH": return plusMonths(anchor, dir);
    case "YEAR": return plusMonths(anchor, 12 * dir);
  }
}

// ── Einträge (D23, D26) ────────────────────────────────────────────────────────────────────────────────────────

/** Die Einträge je Tag im Fenster, jeder an jedem seiner Tage; je Tag „Schlechtestes oben“ (Ordnung der Domäne). */
export function entriesByDay(entries: readonly CalendarEntry[], from: string, to: string): Map<string, CalendarEntry[]> {
  const byDay = new Map<string, CalendarEntry[]>();
  for (const e of entries) {
    const end = e.endDate ?? e.date;
    for (let d = e.date < from ? from : e.date; d <= end && d <= to; d = addDays(d, 1)) {
      const list = byDay.get(d);
      if (list === undefined) byDay.set(d, [e]);
      else list.push(e);
    }
  }
  for (const list of byDay.values()) list.sort((a, b) => compareDueItems(a.item, b.item));
  return byDay;
}

/** D26 mit dem Nachtrag H2: Bei „Tickets an“ tritt ein Fremdeintrag hinter sein Fälligkeitsticket zurück, wenn es am
 *  selben Tag liegt UND in derselben Antwort steht; ein archiviertes, erledigtes oder fremdes Ticket fehlt dort. */
export function visibleEntries(entries: readonly CalendarEntry[], ticketsOn: boolean): CalendarEntry[] {
  if (!ticketsOn) return [...entries];
  const shown = ticketDueIds(entries.map((e) => e.item));
  return entries.filter((e) => !recedesBehindTicket(e.item, shown));
}

/** Die Zustände eines Eintrags, Schlechtestes zuerst (Rangfolge der Ordnung und der Legende). */
export const ENTRY_STATES: readonly CalendarEntry["state"][] = ["OVERDUE", "DUE_TODAY", "OPEN", "DONE"];

/** Der schlechteste Zustand (Hausregel „Schlechtestes oben“: ERROR vor heute vor offen vor erledigt); leer `null`. */
export function worstState(entries: readonly CalendarEntry[]): CalendarEntry["state"] | null {
  return ENTRY_STATES.find((s) => entries.some((e) => e.state === s)) ?? null;
}

// ── Texte ──────────────────────────────────────────────────────────────────────────────────────────────────────

const T = CALENDAR_TEXT;

/** Der Zeitraumtext der Leiste: „Do 05.11.2026“, „KW 45 · 02.–08.11.2026“ (über Monat und Jahr mit beiden Teilen),
 *  „November 2026“, „2026“. */
export function periodText(view: CalendarView, anchor: string): string {
  const [y, m] = parts(anchor);
  switch (view) {
    case "DAY": return T.period.day(T.weekdaysShort[weekday(anchor)]!, fullDate(anchor));
    case "WEEK": {
      const { from, to } = windowOf("WEEK", anchor);
      const start = from.slice(0, 4) !== to.slice(0, 4) ? fullDate(from) : from.slice(5, 7) !== to.slice(5, 7) ? shortDate(from)
        : `${from.slice(8, 10)}.`;
      return T.period.week(isoWeek(from), `${start}–${fullDate(to)}`);
    }
    case "MONTH": return T.period.month(T.months[m - 1]!, y);
    case "YEAR": return String(y);
  }
}

/** Kopf einer Wochenspalte: „Mo 02.11.“. */
export function dayHead(day: string): string {
  return T.period.day(T.weekdaysShort[weekday(day)]!, shortDate(day));
}

/** Vorlesestimme eines Tages im Raster: „Donnerstag, 5. November, 3 Einträge, 1 überfällig“. */
export function daySpeak(day: string, entries: readonly CalendarEntry[]): string {
  const [, m, d] = parts(day);
  return T.daySpeak(T.weekdaysLong[weekday(day)]!, d, T.months[m - 1]!, entries.length,
    entries.filter((e) => e.state === "OVERDUE").length);
}

/** Sofort-Popup eines Jahrestags: „Do 05.11.2026 · 3 Einträge“, dann je Eintrag Quellwort und Nummer bzw. Titel;
 *  über zehn „+n weitere“. */
export function yearDayPopup(day: string, entries: readonly CalendarEntry[]): string {
  const lines = entries.slice(0, YEAR_POPUP_MAX).map((e) => `${DUE_ITEM_TEXT.wordOf(e.item)} ${e.reference ?? e.title}`);
  if (entries.length > YEAR_POPUP_MAX) lines.push(T.moreCount(entries.length - YEAR_POPUP_MAX));
  return [T.yearDayHead(periodText("DAY", day), entries.length), ...lines].join("\n");
}
